import { useState, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { apiClient } from '@services/api';
import Button from '@components/Button';
import TextField from '@components/TextField';
import Icon from '@material-ui/core/Icon';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import { withStyles } from '@material-ui/core/styles';

const FILTER_OPTIONS = [
  { value: 'all',           label: 'Todos os clientes com o app' },
  { value: 'by_date_range', label: 'Por período de reserva' },
  { value: 'by_tour',       label: 'Por passeio específico' },
];

const EMPTY_FORM = {
  title:               '',
  message:             '',
  filter_type:         'all',
  filter_from:         '',
  filter_to:           '',
  tour_id:             '',
  has_discount:        'false',
  discount_percent:    '',
  discount_expires_at: '',
  tour_name:           '',
  promo_code:          '',
};

// Estilos para os inputs ficarem legíveis no fundo branco
const LIGHT_INPUT = {
  InputProps:      { style: { backgroundColor: '#fff', color: '#172B4D' } },
  InputLabelProps: { style: { color: '#64748b' } },
};

// Radio estilizado com a cor do tema
const ThemeRadio = withStyles({
  root: { color: '#94a3b8', '&$checked': { color: '#1a56db' } },
  checked: {},
})((props) => <Radio color="default" {...props} />);

// ─── Componente de busca de passeio ────────────────────────────────────────────
const TourSearchInput = ({ label, initialValue, onSelect }) => {
  const [query,   setQuery]   = useState(initialValue || '');
  const [results, setResults] = useState([]);
  const [open,    setOpen]    = useState(false);
  const [busy,    setBusy]    = useState(false);
  const timer = useRef(null);

  // Atualiza quando initialValue muda (edição)
  useEffect(() => { if (initialValue) setQuery(initialValue); }, [initialValue]);

  const search = (q) => {
    if (!q || q.length < 2) { setResults([]); setOpen(false); return; }
    setBusy(true);
    apiClient.httpInstance
      .get('/tours', { params: { q, per_page: 10 } })
      .then(res => {
        const data = res.data?.data || res.data || [];
        setResults(Array.isArray(data) ? data.slice(0, 10) : []);
        setOpen(true);
      })
      .catch(() => setResults([]))
      .finally(() => setBusy(false));
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (!val) onSelect({ id: null, name: '' });
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(val), 350);
  };

  const handleSelect = (tour) => {
    setQuery(tour.name);
    setOpen(false);
    onSelect(tour);
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <TextField
        className="inputRounded"
        label={label}
        variant="outlined"
        value={query}
        onChange={handleChange}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        InputProps={{
          style: { backgroundColor: '#fff', color: '#172B4D' },
          endAdornment: busy ? <CircularProgress size={16} style={{ color: '#94a3b8' }} /> : null,
        }}
        InputLabelProps={{ style: { color: '#64748b' } }}
      />
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0,
          background: '#fff', borderRadius: 8,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          zIndex: 1000, maxHeight: 220, overflowY: 'auto',
          border: '1px solid #e2e8f0',
        }}>
          {results.map(t => (
            <div
              key={t.id}
              onMouseDown={() => handleSelect(t)}
              style={{ padding: '10px 14px', cursor: 'pointer', color: '#172B4D', fontSize: 14 }}
              onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              {t.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Componente principal ──────────────────────────────────────────────────────
const Create = (props) => {
  const history  = useHistory();
  const { id }   = useParams();
  const isEdit   = !!id;

  const [form, setForm]                     = useState(EMPTY_FORM);
  const [loadingData, setLoadingData]       = useState(isEdit);
  const [previewCount, setPreviewCount]     = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loading, setLoading]               = useState(false);
  const [errors, setErrors]                 = useState({});
  // Labels para exibição nos selects de passeio (edição)
  const [tourFilterLabel,  setTourFilterLabel]  = useState('');
  const [tourDiscountLabel, setTourDiscountLabel] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    setLoadingData(true);
    apiClient.httpInstance
      .get(`/app-broadcasts/${id}`)
      .then(res => {
        const b = res.data.data;
        setForm({
          title:               b.title               || '',
          message:             b.message             || '',
          filter_type:         b.filter_type         || 'all',
          filter_from:         b.filter_from         || '',
          filter_to:           b.filter_to           || '',
          tour_id:             b.tour_id             ? String(b.tour_id) : '',
          has_discount:        b.has_discount         ? 'true' : 'false',
          discount_percent:    b.discount_percent    ? String(b.discount_percent) : '',
          discount_expires_at: b.discount_expires_at || '',
          tour_name:           b.tour_name           || '',
          promo_code:          b.promo_code          || '',
        });
        // Carrega nomes dos passeios para exibição
        if (b.tour_id) {
          apiClient.httpInstance.get(`/tours/${b.tour_id}`)
            .then(r => setTourFilterLabel(r.data?.name || `Tour #${b.tour_id}`))
            .catch(() => setTourFilterLabel(`Tour #${b.tour_id}`));
        }
        if (b.tour_name) setTourDiscountLabel(b.tour_name);
      })
      .catch(() => history.push('/admin/broadcast-app'))
      .finally(() => setLoadingData(false));
  }, [id]);

  const change = (e) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const hasDiscount = form.has_discount === 'true';

  const handlePreview = () => {
    setLoadingPreview(true);
    apiClient.httpInstance
      .get('/app-broadcasts/preview', {
        params: {
          filter_type: form.filter_type,
          filter_from: form.filter_from || undefined,
          filter_to:   form.filter_to   || undefined,
          tour_id:     form.tour_id     || undefined,
        }
      })
      .then(res => setPreviewCount(res.data.count))
      .catch(() => setPreviewCount(null))
      .finally(() => setLoadingPreview(false));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    const payload = {
      ...form,
      has_discount:        hasDiscount,
      discount_percent:    hasDiscount ? form.discount_percent    : undefined,
      discount_expires_at: hasDiscount ? form.discount_expires_at : undefined,
      tour_name:           hasDiscount && form.tour_name.trim() ? form.tour_name.trim() : undefined,
      promo_code:          hasDiscount && form.promo_code.trim() ? form.promo_code.trim().toUpperCase() : undefined,
    };

    const request = isEdit
      ? apiClient.httpInstance.put(`/app-broadcasts/${id}`, payload)
      : apiClient.httpInstance.post('/app-broadcasts', payload);

    request
      .then(() => history.push('/admin/broadcast-app'))
      .catch(err => {
        if (err.response?.data?.errors) setErrors(err.response.data.errors);
      })
      .finally(() => setLoading(false));
  };

  if (loadingData) {
    return (
      <div className="flex justify-center mt-20">
        <CircularProgress style={{ color: props.theme.color }} />
      </div>
    );
  }

  return (
    <div className="px-7 pb-10">
      {/* Header */}
      <div className="flex items-center gap-3 mt-2 mb-6">
        <button
          onClick={() => history.push('/admin/broadcast-app')}
          className="text-gray-400 hover:text-gray-600"
          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <Icon>arrow_back</Icon>
        </button>
        <span className="text-2xl text-blue">
          {isEdit ? 'Editar rascunho' : 'Nova campanha'}
        </span>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="p-4 pt-3 pb-6 bg-white rounded-xl border-1 text-blue font-poppins space-y-5">

          {/* Título */}
          <TextField
            className="inputRounded"
            label="Título da notificação"
            name="title"
            variant="outlined"
            value={form.title}
            onChange={change}
            error={errors.title}
            required
            {...LIGHT_INPUT}
          />

          {/* Mensagem */}
          <TextField
            className="inputRounded"
            label="Mensagem"
            name="message"
            variant="outlined"
            multiline
            rows={3}
            value={form.message}
            onChange={change}
            error={errors.message}
            required
            {...LIGHT_INPUT}
          />

          {/* Filtro de destinatários */}
          <TextField
            className="inputRounded"
            label="Destinatários"
            select
            variant="outlined"
            name="filter_type"
            value={form.filter_type}
            onChange={change}
            {...LIGHT_INPUT}
          >
            {FILTER_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>

          {/* Filtro por período de reserva */}
          {form.filter_type === 'by_date_range' && (
            <div className="flex gap-3">
              <TextField
                className="inputRounded"
                label="Data de reserva — de"
                name="filter_from"
                type="date"
                variant="outlined"
                value={form.filter_from}
                onChange={change}
                error={errors.filter_from}
                InputLabelProps={{ shrink: true, style: { color: '#64748b' } }}
                InputProps={{ style: { backgroundColor: '#fff', color: '#172B4D' } }}
                required
              />
              <TextField
                className="inputRounded"
                label="Data de reserva — até"
                name="filter_to"
                type="date"
                variant="outlined"
                value={form.filter_to}
                onChange={change}
                error={errors.filter_to}
                InputLabelProps={{ shrink: true, style: { color: '#64748b' } }}
                InputProps={{ style: { backgroundColor: '#fff', color: '#172B4D' } }}
                required
              />
            </div>
          )}

          {/* Filtro por passeio específico */}
          {form.filter_type === 'by_tour' && (
            <>
              <TourSearchInput
                label="Buscar passeio para filtrar destinatários"
                initialValue={tourFilterLabel}
                onSelect={tour => {
                  setForm(f => ({ ...f, tour_id: tour.id ? String(tour.id) : '' }));
                  setTourFilterLabel(tour.name || '');
                }}
              />
              <div className="flex gap-3">
                <TextField
                  className="inputRounded"
                  label="Período da reserva — de (opcional)"
                  name="filter_from"
                  type="date"
                  variant="outlined"
                  value={form.filter_from}
                  onChange={change}
                  error={errors.filter_from}
                  InputLabelProps={{ shrink: true, style: { color: '#64748b' } }}
                  InputProps={{ style: { backgroundColor: '#fff', color: '#172B4D' } }}
                />
                <TextField
                  className="inputRounded"
                  label="Período da reserva — até (opcional)"
                  name="filter_to"
                  type="date"
                  variant="outlined"
                  value={form.filter_to}
                  onChange={change}
                  error={errors.filter_to}
                  InputLabelProps={{ shrink: true, style: { color: '#64748b' } }}
                  InputProps={{ style: { backgroundColor: '#fff', color: '#172B4D' } }}
                />
              </div>
            </>
          )}

          {/* Preview de destinatários */}
          <div className="flex items-center gap-3">
            <Button
              textcolor="#FFF"
              padding="6px 15px"
              width="200"
              customcolor={props.theme.color}
              type="button"
              onClick={handlePreview}
              disabled={loadingPreview}
            >
              {loadingPreview
                ? <CircularProgress size={18} style={{ color: '#fff' }} />
                : <><Icon style={{ fontSize: 16, marginRight: 6, verticalAlign: 'middle' }}>people</Icon>Verificar destinatários</>
              }
            </Button>
            {previewCount !== null && !loadingPreview && (
              <span className="text-sm text-gray-500">
                <strong style={{ color: props.theme.color }}>{previewCount}</strong>{' '}
                cliente{previewCount !== 1 ? 's' : ''} receberão esta campanha
              </span>
            )}
          </div>

          {/* Desconto — liga/desliga */}
          <div>
            <label className="text-dark text-sm font-medium block mb-2">Incluir código de desconto</label>
            <p className="text-xs text-gray-400 mb-2">
              O cupom aparece automaticamente no FT App para os destinatários desta campanha.
            </p>
            <FormControl component="fieldset">
              <RadioGroup
                row
                name="has_discount"
                value={form.has_discount}
                onChange={change}
              >
                <FormControlLabel value="true"  control={<ThemeRadio />} label="Sim" />
                <FormControlLabel value="false" control={<ThemeRadio />} label="Não" />
              </RadioGroup>
            </FormControl>

            {hasDiscount && (
              <div className="mt-3 space-y-3">
                <div className="flex gap-3">
                  <TextField
                    className="inputRounded"
                    label="% de desconto"
                    name="discount_percent"
                    type="number"
                    variant="outlined"
                    value={form.discount_percent}
                    onChange={change}
                    error={errors.discount_percent}
                    required
                    {...LIGHT_INPUT}
                  />
                  <TextField
                    className="inputRounded"
                    label="Válido até"
                    name="discount_expires_at"
                    type="date"
                    variant="outlined"
                    value={form.discount_expires_at}
                    onChange={change}
                    error={errors.discount_expires_at}
                    InputLabelProps={{ shrink: true, style: { color: '#64748b' } }}
                    InputProps={{ style: { backgroundColor: '#fff', color: '#172B4D' } }}
                    required
                  />
                </div>
                <TourSearchInput
                  label="Passeio específico do desconto (opcional — deixe vazio para todos)"
                  initialValue={tourDiscountLabel}
                  onSelect={tour => {
                    setForm(f => ({ ...f, tour_name: tour.name || '' }));
                    setTourDiscountLabel(tour.name || '');
                  }}
                />
                <div>
                  <TextField
                    className="inputRounded"
                    label="Código promocional (opcional — ex: 95OFF)"
                    name="promo_code"
                    variant="outlined"
                    value={form.promo_code}
                    onChange={change}
                    error={errors.promo_code}
                    inputProps={{ maxLength: 32, style: { textTransform: 'uppercase' } }}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Se preenchido, o cliente poderá digitar este código no checkout mesmo sem ter recebido a notificação.
                    Deixe em branco para que o desconto seja aplicado apenas automaticamente via broadcast.
                  </p>
                </div>
              </div>
            )}
          </div>

        </div>

        <div className="text-right mt-4">
          <Button
            textcolor="#FFF"
            padding="6px 15px"
            width="200"
            customcolor={props.theme.color}
            type="submit"
            disabled={loading}
          >
            {loading
              ? <CircularProgress size={18} style={{ color: '#fff' }} />
              : isEdit ? 'Salvar alterações' : 'Salvar rascunho'
            }
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Create;
